import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const About = () => {
  usePageTitle('Sobre Nós');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  return (
    <div data-testid="about-page" className="bg-white">
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <p
            data-testid="about-eyebrow"
            className="font-accent text-[11px] tracking-[0.35em] uppercase text-neutral-500 mb-6"
          >
            Casa in Gold · Sobral · Desde 2015
          </p>
          <h1
            data-testid="about-title"
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05] text-neutral-900 max-w-4xl"
          >
            Com dez anos de história, <span className="italic text-neutral-700">Casa in Gold</span> expande atuação em Sobral.
          </h1>
          <div className="mt-8 h-px w-24 bg-neutral-900/70" />
        </div>
      </section>

      {/* Article — Bloco 1 (texto + poltrona) */}
      <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="text-base md:text-[17px] font-light leading-[1.85] text-neutral-700 mb-6">
              Fundada há dez anos em Sobral, a <strong className="font-medium text-neutral-900">Casa in Gold</strong> nasceu com o desejo
              de transformar o ato de comprar móveis em algo mais profundo. À frente do negócio,
              a empresária e curadora de design <strong className="font-medium text-neutral-900">Fhiama Fonseca</strong> construiu uma marca
              que une estética, qualidade e propósito.
            </p>
            <p className="text-base md:text-[17px] font-light leading-[1.85] text-neutral-700 mb-8">
              Com o passar do tempo, esse sonho cresceu, ganhou novas formas e novos caminhos.
              Em 2025, nasceu o <strong className="font-medium text-neutral-900">Grupo Casa</strong>, um universo criativo que reúne
              diferentes expressões do nosso trabalho e da nossa identidade.
            </p>
            <blockquote
              data-testid="about-quote-1"
              className="border-l-2 border-neutral-900 pl-6 py-2 my-8"
            >
              <p className="font-heading italic text-xl md:text-2xl leading-snug text-neutral-900">
                "Estou sempre em busca do melhor, porque para mim o objetivo não é apenas
                entregar móveis, é realizar sonhos."
              </p>
              <footer className="mt-4 font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500">
                — Fhiama Fonseca
              </footer>
            </blockquote>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <figure className="relative">
              <img
                data-testid="about-image-chair"
                src={settings?.about_page_chair_image || "/about-fhiama-chair.jpg"}
                alt="Fhiama Fonseca sentada em poltrona autoral da Casa in Gold"
                className="w-full h-auto object-cover shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)]"
              />
              <figcaption className="mt-4 font-accent text-[10px] tracking-[0.3em] uppercase text-neutral-500">
                Fhiama Fonseca · Fundadora & Curadora
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Article — Bloco 2 (lampada + texto) */}
      <section className="bg-neutral-50 py-20 md:py-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <figure className="relative">
              <img
                data-testid="about-image-lamp"
                src={settings?.about_page_lamp_image || "/about-fhiama-lamp.jpg"}
                alt="Fhiama Fonseca em curadoria de peças de design"
                className="w-full h-auto object-cover shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)]"
              />
            </figure>
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-neutral-900 mb-8 leading-tight">
              O design vai além <br className="hidden md:block" />da aparência.
            </h2>
            <p className="text-base md:text-[17px] font-light leading-[1.85] text-neutral-700 mb-6">
              Segundo Fhiama, o design vai além da aparência. A atuação direta da empresária
              na escolha das peças, no relacionamento com arquitetos e no atendimento aos
              clientes é um dos pilares do negócio. Cada projeto passa por uma escuta atenta
              e por uma construção conceitual coerente.
            </p>
            <blockquote
              data-testid="about-quote-2"
              className="border-l-2 border-neutral-900 pl-6 py-2 my-8"
            >
              <p className="font-heading italic text-xl md:text-2xl leading-snug text-neutral-900">
                "Acredito que um lar é construído por histórias, memórias e afetos.
                Por isso, cada projeto que passa por nós carrega intenção, cuidado e personalidade."
              </p>
            </blockquote>
            <p className="text-base md:text-[17px] font-light leading-[1.85] text-neutral-700">
              A proposta é clara: criar ambientes que façam sentido para quem vive ali,
              preservando a identidade e as histórias de cada cliente.
            </p>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-accent text-[11px] tracking-[0.35em] uppercase text-neutral-500 mb-4">
              Nossos Pilares
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-neutral-900">
              O que nos move
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            <div data-testid="value-curadoria" className="text-center md:text-left">
              <span className="font-heading text-5xl font-light text-neutral-300">01</span>
              <h3 className="font-heading text-xl mt-4 mb-3 text-neutral-900">Curadoria</h3>
              <p className="text-sm font-light leading-relaxed text-neutral-600">
                Cada peça é escolhida a dedo por Fhiama, unindo estética, propósito e o rigor
                de um olhar que pensa antes na história do lar.
              </p>
            </div>
            <div data-testid="value-artesania" className="text-center md:text-left">
              <span className="font-heading text-5xl font-light text-neutral-300">02</span>
              <h3 className="font-heading text-xl mt-4 mb-3 text-neutral-900">Artesania</h3>
              <p className="text-sm font-light leading-relaxed text-neutral-600">
                Técnica tradicional e inovação contemporânea caminham juntas em cada móvel
                assinado pela Casa in Gold.
              </p>
            </div>
            <div data-testid="value-proposito" className="text-center md:text-left">
              <span className="font-heading text-5xl font-light text-neutral-300">03</span>
              <h3 className="font-heading text-xl mt-4 mb-3 text-neutral-900">Propósito</h3>
              <p className="text-sm font-light leading-relaxed text-neutral-600">
                Não vendemos móveis, realizamos sonhos. Cada projeto carrega intenção,
                cuidado e a assinatura de quem vive ali.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-neutral-900 text-white px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-accent text-[11px] tracking-[0.35em] uppercase text-neutral-400 mb-6">
            Grupo Casa · Sobral
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-8 leading-tight">
            Vamos criar juntos <span className="italic">o seu lar</span>?
          </h2>
          <p className="text-base md:text-lg font-light leading-relaxed text-neutral-300 mb-10 max-w-2xl mx-auto">
            Conte com uma curadoria dedicada e um atendimento próximo para transformar
            o seu ambiente em uma extensão da sua história.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              data-testid="about-cta-catalog"
              to="/catalog"
              className="inline-block bg-white text-neutral-900 px-10 py-4 font-accent text-xs tracking-[0.25em] uppercase hover:bg-neutral-200 transition-colors"
            >
              Ver Catálogo
            </Link>
            <Link
              data-testid="about-cta-contact"
              to="/contact"
              className="inline-block border border-white/40 text-white px-10 py-4 font-accent text-xs tracking-[0.25em] uppercase hover:bg-white hover:text-neutral-900 transition-colors"
            >
              Falar com a Casa
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
